import { apiRequest } from "./client";

// Mirrors Stripe's subscription status verbatim, plus our own 'inactive'
// for parents who haven't started a checkout yet. The FE typically only
// cares about `isActive` — derived server-side.
export type SubscriptionStatus =
  | "inactive"
  | "trialing"
  | "active"
  | "past_due"
  | "canceled"
  | "incomplete"
  | "incomplete_expired"
  | "unpaid";

// The two plans we ship today. The backend resolves these to Stripe
// Price IDs via STRIPE_PRICE_ID_MONTHLY / STRIPE_PRICE_ID_ANNUAL env
// vars. `null` means the parent's current Price doesn't match either
// env value (legacy install, manual portal swap, etc.) — the FE shows
// a neutral "Kidly+" label in that case.
export type PlanInterval = "monthly" | "annual";

export type SubscriptionState = {
  status: SubscriptionStatus;
  // True when status is one of {trialing, active}. The FE uses this as
  // the single source of truth for gating premium content.
  isActive: boolean;
  trialEndsAt: string | null;
  renewsAt: string | null;
  canceledAt: string | null;
  plan: PlanInterval | null;
  // True when the parent is eligible for the no-card 30-day free trial
  // (never trialed, no Stripe history, not currently active). Drives the
  // "Start free trial" CTA. Optional for back-compat with older servers.
  canStartFreeTrial?: boolean;
};

export const subscriptionsApi = {
  // Read the parent's current entitlement. DB-only on the server, so cheap
  // enough to call on app start + after returning from a Stripe redirect.
  me: (token: string): Promise<SubscriptionState> =>
    apiRequest<SubscriptionState>("/v1/subscriptions/me", { token }),

  // Open a Stripe-hosted Checkout session and return its URL. The caller
  // is expected to redirect (window.location.href = url) so Stripe can take
  // the parent through card capture in their iframe-free hosted page.
  // `plan` defaults to monthly server-side when omitted.
  checkout: (
    token: string,
    plan?: PlanInterval,
  ): Promise<{ url: string }> =>
    apiRequest<{ url: string }>("/v1/subscriptions/checkout", {
      method: "POST",
      body: plan ? { plan } : undefined,
      token,
    }),

  // Start the no-card 30-day free trial. Grants instant access without a
  // Stripe redirect; returns the refreshed SubscriptionState so the caller
  // can drop it straight into the auth store.
  startTrial: (token: string): Promise<SubscriptionState> =>
    apiRequest<SubscriptionState>("/v1/subscriptions/trial", {
      method: "POST",
      token,
    }),

  // Customer Portal URL — Stripe-hosted UI for card update / cancel /
  // resubscribe. Same redirect pattern as checkout().
  portal: (token: string): Promise<{ url: string }> =>
    apiRequest<{ url: string }>("/v1/subscriptions/portal", {
      method: "POST",
      token,
    }),

  // Swap an existing subscription's plan. Pro-rates on Stripe's side —
  // the parent isn't sent back through checkout. Returns the refreshed
  // SubscriptionState so the caller can replace its local snapshot.
  switchPlan: (
    plan: PlanInterval,
    token: string,
  ): Promise<SubscriptionState> =>
    apiRequest<SubscriptionState>("/v1/subscriptions/switch", {
      method: "POST",
      body: { plan },
      token,
    }),
};
